import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line import/no-extraneous-dependencies
import { CustomTagProps } from 'rc-select/lib/BaseSelect';
// eslint-disable-next-line import/no-extraneous-dependencies
import { ChangeEventExtra } from 'rc-tree-select/es/interface';
import { ChevronDownIcon, ChevronUpIcon } from 'assets/icons';
import { STreeSelect, TreeSelectStyles } from 'components/Sort/Sort.styled';
import { Flex, Icon, Text } from 'UI';
import { ASCENDING, SortData, TreeDataNode, TreeSelectSortInputProps } from './Sort.types';

const TreeSelectSortInput = <T,>({
  handleSetSort,
  handleResetSort,
  treeData,
  initSort,
}: TreeSelectSortInputProps<T>) => {
  const { t } = useTranslation();
  const [selectedValues, setSelectedValues] = useState<
    {
      value: string;
      label: React.ReactNode;
    }[]
  >([]);
  const [sortInitialized, setSortInitialized] = useState<boolean>(false);

  useEffect(() => {
    if (!sortInitialized && initSort.length > 0) {
      const checkedNodes = treeData.filter((node: TreeDataNode<T>) => initSort.includes(node.value));
      setSelectedValues(
        checkedNodes.map((node: TreeDataNode<T>) => ({
          value: node.value,
          label: node.title,
        })),
      );
      handleSetSort(
        checkedNodes.map(
          (node: TreeDataNode<T>): SortData<T> => ({
            ...node.sortBy,
            key: node.value,
          }),
        ),
      );
      setSortInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initSort]);

  useEffect(() => {
    return () => {
      handleResetSort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enhancedTagRender = (tagProps: CustomTagProps) => {
    const originalNode = treeData.find((node) => node.value === tagProps.value);
    const enhancedProps = {
      ...tagProps,
      ...originalNode,
    };
    const direction = enhancedProps?.sortBy?.direction;

    return (
      <Flex align={'center'} padding={'0px 5px 0px 5px'} gap={5}>
        {direction && (
          <Icon IconComponent={direction === ASCENDING ? ChevronUpIcon : ChevronDownIcon} size={20} noPointer />
        )}
        <Text type="base" disableGutter>
          {enhancedProps.shortTitle}
        </Text>
      </Flex>
    );
  };

  const handleChange = (
    newValues: {
      value: string;
      label: React.ReactNode;
    }[],
    _label: React.ReactNode[],
    extra: ChangeEventExtra,
  ) => {
    if (newValues.length === 0) {
      setSelectedValues([]);
      handleSetSort([]);
    } else {
      const triggerNode = treeData.find((node) => node.value === extra.triggerValue);
      const filteredNodes = newValues
        .map((e) => treeData.find((node) => node.value === e.value))
        .filter(
          (node) =>
            node &&
            triggerNode &&
            (triggerNode.value === node.value || triggerNode.sectionPairNumber !== node.sectionPairNumber),
        ) as TreeDataNode<T>[];

      setSelectedValues(
        filteredNodes.map((node) => ({
          value: node.value,
          label: node.title,
        })),
      );
      handleSetSort(
        filteredNodes.map(
          (node): SortData<T> => ({
            ...node.sortBy,
            key: node.value,
          }),
        ),
      );
    }
  };

  return (
    <>
      <TreeSelectStyles />
      <STreeSelect
        treeData={treeData}
        value={selectedValues}
        onChange={handleChange as (newValues: unknown, label: React.ReactNode[], extra: ChangeEventExtra) => void}
        treeCheckable={true}
        multiple={true}
        treeCheckStrictly={true}
        showSearch={false}
        placeholder={t('catalog.tires.sort.sort_by', 'Sort by')}
        labelInValue={true}
        treeDefaultExpandAll={true}
        tagRender={enhancedTagRender}
        popupClassName="custom-select-tree-dropdown"
      />
    </>
  );
};

export default TreeSelectSortInput;
